import { IAppUpdate, IResponse, ITodoList, ITodoTask, IValueData, SectionId, TItineraryEntry, V2 } from "@eventr/common";
import { ScreenType } from "../../v2/renderers/rendererLocator";
import { DraftItem } from "../../utils/merge-draft-items";
import { IItinerary } from "../interfaces";

export type SectionUpdateResult = "success" | "private_data_error" | "error";

interface CurrentItinState {
  selectedItinerary: IItinerary | undefined;
  eventEnded: boolean;
  past: boolean;
  itineraryComponents: { past: any; present: any; todos: ITodoList[] }; /// any as is obj. keys are dates, values are items in that date.
  /**
   * @deprecated use selectIsItineraryEmpty
   */
  selectedItinEmpty: boolean;
  selectedItinID: number;
  apiUpdates?: IAppUpdate;
  // these are values for edit dont use them to render them in home screen or team only edit pages
  editValues?: IValueData;
  // The last attempt to load editValues ended without them (failed, not allowed, or no
  // itinerary), so their absence is not a load in progress. Session-only, not persisted.
  editValuesUnavailable: boolean;
  lastUsedDateTime?: string;
  timelineEntries?: any;
  // Unsaved item edits, keyed by itinerary id and then by section id. Keying by itinerary
  // is what makes a cross-itinerary read inexpressible, rather than merely prevented by
  // clearing on switch; setCurrentItin still clears, but as a separate choice about how
  // long unsaved work lives.
  draftSectionItems: Record<number, Partial<Record<SectionId, DraftItem[]>>>;
  // Staging time (ms epoch) per draft item, used to prune drafts older than 24h at rehydrate.
  draftSectionItemStagedAt: Record<number, Partial<Record<SectionId, Record<string, number>>>>;
}

interface CurrentItinActions {
  // Discards drafts and map selection, then loads.
  setCurrentItin: (id: number) => Promise<void>;
  // Starts fetching an itinerary if its cached copy is missing or stale. A later
  // loadItinerary of the same itinerary joins the fetch.
  prefetchItinerary: (id: number) => void;
  // Reloads selectedItinID with no switch side effects. Refetches only when the itinerary
  // map's version differs, so refresh the map first.
  refreshCurrentItinerary: () => Promise<void>;
  loadItinerary: (id: number) => Promise<void>;
  setPast: (past: boolean) => void;
  getEventInfoV2: () => V2 | undefined;
  getCurrentEventImageUrl: () => string | undefined;
  filterEntitiesUserSpecific: (screenType: ScreenType, data: V2, userList?: string, viewedUserId?: number | null) => any[];
  getSectionV2FromSectionId: (sectionId: number) => V2 | undefined;
  updateItinerarySection: (sectionId: number, newSectionData: TItineraryEntry) => Promise<SectionUpdateResult>;
  deleteItineraryItem: (sectionId: number, itemId: string) => Promise<boolean>;
  getV2Value: (valueLocator: string, data: any) => any;
  getSectionV2FromSectionIdFromSelectedItinerary: (sectionId: number) => V2 | undefined;
  getApiUpdates: () => any;
  removePublicItinerary: (code: string) => Promise<void>;
  updateTodo: (task: ITodoTask, groupId: string, itineraryItemId: string) => Promise<void>;
  pullEditValues: () => Promise<void>;
  ensureEditValues: () => Promise<void>;
  upsertElement: (sectionID: number, element: any) => Promise<IResponse<any>>;
  addDraftSectionItem: (sectionId: SectionId, item: DraftItem) => Promise<void>;
  removeDraftSectionItem: (sectionId: SectionId, itemId: string) => Promise<void>;
  removeAllDraftSectionItems: (sectionId: SectionId) => Promise<void>;
  deleteElement: (sectionID: number, elementID?: string) => Promise<IResponse<any>>;
  setLastUsedDateTime: (dateTime: string) => void;
}

export interface CurrentItinSlice extends CurrentItinState, CurrentItinActions {}

export const defaultCurrentItinSlice: CurrentItinState = {
  selectedItinID: -1,
  selectedItinerary: undefined,
  eventEnded: false,
  past: false,
  itineraryComponents: { past: {}, present: {}, todos: [] },
  selectedItinEmpty: false,
  apiUpdates: undefined,
  lastUsedDateTime: undefined,
  draftSectionItems: {},
  draftSectionItemStagedAt: {},
  editValuesUnavailable: false,
};
